import styled from '@emotion/styled';
import React, { type ReactNode, type ReactElement, Fragment } from 'react';
import { Box, Text } from 'rebass';

/** Represents a single key-value entry in a definitions list. */
export type DefinitionItem = {
  /** Unique identifier used as the React list key. */
  readonly key: string;
  /** Human-readable label displayed as the term (`dt`). */
  readonly label: string;
  /** Value displayed as the definition (`dd`). Falls back to `'-'` when omitted. */
  readonly value?: ReactNode;
};

/** Props for the {@link Definitions} component. */
export type DefinitionsProps = {
  /** Ordered list of definition items to render. */
  readonly items: ReadonlyArray<DefinitionItem>;
};

/**
 * Renders a styled two-column definition list (`dl`) from an array of
 * {@link DefinitionItem} entries. Labels appear on the left and values on the
 * right, aligned to the baseline. Missing values render as `'-'`.
 */
export function Definitions({ items }: DefinitionsProps): ReactElement {
  return (
    <DefinitionList as="dl">
      {items.map(({ key, label, value }) => (
        <Fragment key={key}>
          <Text as="dt" variant="body2" color="fieldBorderHover">
            {label}
          </Text>
          <DefinitionValue as="dd" variant="body2">
            {value ?? '-'}
          </DefinitionValue>
        </Fragment>
      ))}
    </DefinitionList>
  );
}

const DefinitionList = styled(Box)`
  display: grid;
  grid-template-columns: repeat(2, auto);
  column-gap: 8px;
  row-gap: 16px;
  padding-block: 8px;
  align-items: baseline;
`;

const DefinitionValue = styled(Text)`
  margin: 0;
  text-align: right;
  overflow-wrap: break-word;
`;
